import { XMarkIcon } from '@heroicons/react/24/outline'
import { AnimatePresence, motion } from 'framer-motion'
import PropTypes from 'prop-types'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import { SH, SW } from './stage'

/**
 * Huelle fuer alle Launcher-Varianten: fixer Bereich unten rechts, unsichtbarer
 * Button ueber der Klammer/Figur, Hinweis nach dem Intro und Chat-Popout.
 *
 * Eine Variante liefert { id, introEnd, storageKey?, tooltip, Stage, Avatar }.
 * Stage rendert SVG/HTML in die Buehne und stellt per Ref frame(t, ui) bereit.
 * ui enthaelt geglaettete Werte: hover, open, thinking (0..1), px/py (Maus relativ
 * zur Button-Mitte in px) und near (0..1, wie nah die Maus ist).
 */

const readSeen = (key) => {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch (e) {
    return false
  }
}
const writeSeen = (key) => {
  try {
    window.localStorage.setItem(key, '1')
  } catch (e) {}
}

const LauncherShell = forwardRef(function LauncherShell(
  {
    variant,
    intro = 'once',
    title = 'Aivy Assistent',
    subtitle = 'Kennt eure Rollen, Profile und Ergebnisse',
    userName,
    tooltipText = 'Frag mich alles zu deinen Talenten, Rollen und Ergebnissen.',
    thinking = false,
    defaultOpen = false,
    onOpenChange,
    children
  },
  ref
) {
  const { Stage, Avatar, introEnd } = variant
  const greeting = `Hi${userName ? ` ${userName}` : ''}, ich bin ${
    variant.evolved ? 'jetzt ' : ''
  }dein persönlicher Assistent.`
  const storageKey =
    variant.storageKey || `aivy.assistantLauncher.${variant.id}.introSeen`

  const stageRef = useRef(null)
  const btnRef = useRef(null)
  const tRef = useRef(introEnd + 1)
  const uiRef = useRef({
    hover: 0,
    hoverTarget: 0,
    open: 0,
    thinking: 0,
    // Maus startet weit weg, bis die erste Bewegung kommt
    px: -2000,
    py: -2000,
    tx: -2000,
    ty: -2000,
    near: 0
  })
  const [open, setOpenState] = useState(defaultOpen)
  const [tooltip, setTooltip] = useState(false)
  const tooltipShownRef = useRef(false)
  const openRef = useRef(open)
  openRef.current = open
  const thinkingRef = useRef(thinking)
  thinkingRef.current = thinking

  const setOpen = useCallback(
    (next) => {
      setOpenState(next)
      setTooltip(false)
      onOpenChange && onOpenChange(next)
    },
    [onOpenChange]
  )

  const startIntro = useCallback(() => {
    tRef.current = 0
    tooltipShownRef.current = false
    setTooltip(false)
  }, [])

  useImperativeHandle(ref, () => ({
    replay: startIntro,
    open: () => setOpen(true),
    close: () => setOpen(false)
  }))

  // Intro einmal pro Browser (oder immer / nie), reduced motion ueberspringt
  useEffect(() => {
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const play =
      !reduce &&
      (intro === 'always' || (intro === 'once' && !readSeen(storageKey)))
    tRef.current = play ? 0 : introEnd + 1
    // Intro-Modus gilt ab Mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Maus relativ zur Button-Mitte
  useEffect(() => {
    const onMove = (e) => {
      const b = btnRef.current
      if (!b) return
      const r = b.getBoundingClientRect()
      uiRef.current.tx = e.clientX - (r.left + r.width / 2)
      uiRef.current.ty = e.clientY - (r.top + r.height / 2)
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => window.removeEventListener('pointermove', onMove)
  }, [])

  // Animations-Loop (Intro und Idle)
  useEffect(() => {
    let raf = 0
    let last = performance.now()
    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000)
      last = now
      const wasIntro = tRef.current < introEnd
      tRef.current += dt
      if (wasIntro && tRef.current >= introEnd) {
        writeSeen(storageKey)
        if (!tooltipShownRef.current && !openRef.current) {
          tooltipShownRef.current = true
          setTooltip(true)
        }
      }
      const ui = uiRef.current
      const k = 1 - Math.exp(-dt * 14)
      const kSlow = 1 - Math.exp(-dt * 6)
      ui.hover += (ui.hoverTarget - ui.hover) * k
      ui.open += ((openRef.current ? 1 : 0) - ui.open) * k
      ui.thinking += ((thinkingRef.current ? 1 : 0) - ui.thinking) * kSlow
      ui.px += (ui.tx - ui.px) * k
      ui.py += (ui.ty - ui.py) * k
      const nearTarget = Math.max(
        0,
        Math.min(1, 1 - (Math.hypot(ui.tx, ui.ty) - 40) / 260)
      )
      ui.near += (nearTarget - ui.near) * kSlow
      stageRef.current && stageRef.current.frame(tRef.current, ui)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hinweis blendet sich nach ein paar Sekunden selbst aus
  useEffect(() => {
    if (!tooltip) return undefined
    const id = setTimeout(() => setTooltip(false), 9000)
    return () => clearTimeout(id)
  }, [tooltip])

  // Escape schliesst den Chat
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  const onButtonClick = () => {
    if (tRef.current < introEnd) tRef.current = introEnd
    writeSeen(storageKey)
    setOpen(!open)
  }

  return (
    <>
      <div
        className='pointer-events-none fixed bottom-6 right-6 z-[60]'
        style={{ width: SW, height: SH }}
      >
        <Stage ref={stageRef} />

        <AnimatePresence>
          {tooltip && !open && (
            <motion.div
              initial={{ opacity: 0, x: 12, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 8, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              className='pointer-events-auto absolute bottom-3 right-[80px] w-64 cursor-pointer rounded-2xl rounded-br-md border border-gray-200 bg-white py-3 pl-4 pr-8 text-sm leading-normal text-gray-700'
              style={{ transformOrigin: '100% 100%' }}
              onClick={() => setOpen(true)}
            >
              <span className='font-medium text-gray-900'>{greeting}</span>{' '}
              {tooltipText}
              {/* Sprechblasen-Spitze Richtung Assistent */}
              <span className='absolute -right-[7px] bottom-3 h-3 w-3 rotate-45 border-r border-t border-gray-200 bg-white' />
              <button
                type='button'
                aria-label='Hinweis schließen'
                className='absolute right-2 top-2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                onClick={(e) => {
                  e.stopPropagation()
                  setTooltip(false)
                }}
              >
                <XMarkIcon className='h-4 w-4' />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          ref={btnRef}
          type='button'
          aria-label={open ? `${title} schließen` : `${title} öffnen`}
          aria-expanded={open}
          className='pointer-events-auto absolute bottom-0 right-0 h-16 w-16 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#407BFF] focus-visible:ring-offset-2'
          onClick={onButtonClick}
          onMouseEnter={() => (uiRef.current.hoverTarget = 1)}
          onMouseLeave={() => (uiRef.current.hoverTarget = 0)}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            role='dialog'
            aria-label={title}
            initial={{ opacity: 0, scale: 0.9, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 12 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            style={{ transformOrigin: '100% 100%' }}
            className='fixed bottom-[104px] right-6 z-[60] flex h-[min(600px,calc(100vh-140px))] w-[min(384px,calc(100vw-48px))] flex-col overflow-hidden rounded-[20px] border border-gray-200 bg-white'
          >
            <div className='flex items-center gap-3 border-b border-gray-100 px-4 py-3'>
              <Avatar size={36} />
              <div className='min-w-0 flex-1'>
                <div className='text-base font-semibold leading-tight text-gray-900'>
                  {title}
                </div>
                <div className='flex items-center gap-1.5 truncate text-xs text-gray-500'>
                  <span className='h-1.5 w-1.5 rounded-full bg-[#43DBBD]' />
                  {subtitle}
                </div>
              </div>
              <button
                type='button'
                aria-label='Chat schließen'
                className='rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                onClick={() => setOpen(false)}
              >
                <XMarkIcon className='h-5 w-5' />
              </button>
            </div>
            <div className='flex min-h-0 flex-1 flex-col'>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
})

LauncherShell.propTypes = {
  variant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    introEnd: PropTypes.number.isRequired,
    storageKey: PropTypes.string,
    evolved: PropTypes.bool,
    Stage: PropTypes.elementType.isRequired,
    Avatar: PropTypes.elementType.isRequired
  }).isRequired,
  intro: PropTypes.oneOf(['once', 'always', 'never']),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  userName: PropTypes.string,
  tooltipText: PropTypes.string,
  thinking: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node
}

export default LauncherShell
